import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardUtils } from '@/features/dashboard/utils/dashboardUtils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useDashboardTotalOwnersApi } from '@/features/dashboard/hooks/useDashboardTotalOwnersApi';
import DashboardTotalOwnersBlockSkeleton from '@/features/dashboard/components/DashboardTotalOwners/DashboardTotalOwnersBlockSkeleton';

type Props = {
	isMobile?: boolean;
};

const DashboardTotalOwnersBlock: FC<Props> = ({ isMobile }) => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { totalOwnersData, isLoadingTotalOwnersData } =
		useDashboardTotalOwnersApi();

	const {
		icon: Icon,
		iconClasses,
		iconBgClasses,
		chartMainColor,
	} = DashboardUtils.getAllChartsConfig('totalOwners');

	return (
		<>
			{!!totalOwnersData?.byApartmentCount && (
				<div
					className={cn(
						'p-6 flex grow  flex-col gap-6 border border-popover-100 rounded-2.5xl bg-popover-10 select-none',
						isMobile && 'p-0 border-0 bg-popover-30 gap-4',
					)}>
					{!isMobile && (
						<div className='flex items-center gap-4'>
							<div
								className={cn(
									'size-8 flex items-center justify-center rounded-semi-xl',
									iconBgClasses,
								)}>
								<Icon className={iconClasses} />
							</div>
							<h4 className='text-xl font-medium'>
								{t(
									'main.dashboard.components.chart.totalOwners.title',
								)}
							</h4>
						</div>
					)}
					<div>
						<h3
							className={cn(
								'text-2xl font-medium text-popover-1000',
								isMobile && 'text-xl',
							)}>
							{totalOwnersData?.totalOwners}
						</h3>
					</div>
					<div className='grow-1 flex flex-col items-center gap-6'>
						<div className='w-full h-4 flex gap-0.5 rounded-full overflow-hidden'>
							{totalOwnersData?.byApartmentCount?.map(
								(owner, index) => (
									<div
										key={index}
										className={cn('h-ful', chartMainColor)}
										style={{
											filter: `opacity(${
												1 - index * 0.15
											})`,
											width: `${
												(owner.ownerCount /
													totalOwnersData.totalOwners) *
												100
											}%`,
										}}
									/>
								),
							)}
						</div>
						<div className='w-full h-60 flex flex-col gap-6 overflow-y-scroll'>
							{totalOwnersData?.byApartmentCount?.map(
								(type, index) => (
									<div
										key={index}
										className='flex items-center gap-2.5'>
										<div
											className={cn(
												'size-2 m-1.5 rounded-full',
												chartMainColor,
											)}
											style={{
												filter: `opacity(${
													1 - index * 0.15
												})`,
											}}
										/>
										<span className='text-sm text-popover-1000'>
											{type.apartmentCount}
										</span>
										<span className='text-sm text-popover-1000 ml-auto'>
											{type.ownerCount}
										</span>
									</div>
								),
							)}
						</div>
					</div>
					<Button
						variant={'outline'}
						className='w-full text-sm font-medium'
						onClick={() => navigate('/owners')}>
						{t(
							'main.dashboard.components.chart.totalOwners.seeMoreButton',
						)}
					</Button>
				</div>
			)}
			{!!isLoadingTotalOwnersData && (
				<DashboardTotalOwnersBlockSkeleton />
			)}
		</>
	);
};

export default DashboardTotalOwnersBlock;
